import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import "./authform.component.css";
import Navbar from "../Common/Navbar";

const ForgotPasswordComponent = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [toast, setToast] = useState(null);

  const API_BASE = "http://localhost:8082/api/seo-user";

  // Toast component
  const Toast = ({ message, type, onClose }) => {
    useEffect(() => {
      const timer = setTimeout(() => {
        onClose();
      }, 4000);

      return () => clearTimeout(timer);
    }, [onClose]);

    const getIcon = () => {
      switch (type) {
        case 'success':
          return 'fa-check-circle';
        case 'error':
          return 'fa-times-circle';
        case 'warning':
          return 'fa-exclamation-triangle';
        default:
          return 'fa-info-circle';
      }
    };

    return (
      <div className={`toast-notification toast-${type}`}>
        <div className="toast-icon">
          <i className={`fas ${getIcon()}`}></i>
        </div>
        <div className="toast-content">
          <p className="toast-message">{message}</p>
        </div>
        <button className="toast-close" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>
      </div>
    );
  };

  // Toast helper function
  const showToast = (message, type) => {
    setToast({ message, type });
  };

  const closeToast = () => {
    setToast(null);
  };

  // Password validation rules
  const passwordRules = {
    minLength: newPassword.length >= 8,
    hasUppercase: /[A-Z]/.test(newPassword),
    hasLowercase: /[a-z]/.test(newPassword),
    hasNumber: /[0-9]/.test(newPassword),
    hasSpecial: /[@#$!%*?&]/.test(newPassword)
  };

  const isPasswordValid = Object.values(passwordRules).every(rule => rule);

  // STEP 1 : Send OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();

    if (!email) {
      showToast("Email is required", "warning");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE}/changePasswordOtp`,
        null,
        { params: { username: email } }
      );

      showToast(response.data || "OTP sent to your registered email.", "success");
      setStep(2);
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to send OTP.", "error");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // STEP 2 : Validate OTP + Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!otp || !newPassword || !confirmPassword) {
      showToast("All fields are required", "warning");
      return;
    }

    if (!isPasswordValid) {
      showToast("Password does not meet the required criteria", "warning");
      return;
    }

    if (newPassword !== confirmPassword) {
      showToast("Passwords do not match", "warning");
      return;
    }

    const payload = {
      userName: email,
      otp: otp,
      newPassword: newPassword
    };

    setIsLoading(true);
    try {
      const response = await axios.post(`${API_BASE}/validateOtp`, payload);

      showToast(response.data || "Password reset successfully!", "success");
      
      // Navigate to login after 1.5 seconds
      setTimeout(() => {
        navigate("/login");
      }, 1500);
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to reset password.", "error");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (

    <>
      <Navbar />
    <div className="min-vh-100 d-flex align-items-center cs-color">
      {/* Toast Notification */}
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={closeToast} 
        />
      )}

      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-5">
            <div className="card shadow-lg border-0 cs-br-20">
              <div className="card-body p-5">

                <div className="text-center mb-4">
                  <img
                    src="/logo1.png"
                    alt="ElexytSEO"
                    width="80"
                    height="60"
                    className="mb-3"
                  />
                  <h2 className="fw-bold text-dark mb-2">Forgot Password</h2>
                  <p className="text-muted">
                    {step === 1
                      ? "Enter your registered email to receive OTP"
                      : "Enter OTP and new password"}
                  </p>
                </div>

                {/* STEP 1 – Email */}
                {step === 1 && (
                  <form onSubmit={handleSendOtp}>
                    <div className="mb-4">
                      <input
                        type="email"
                        placeholder="Registered email"
                        className="form-control cs-input"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={isLoading}
                      />
                    </div>

                    <button
                      type="submit"
                      className="btn w-100 py-3 mb-3 cs-sendbtn"
                      disabled={isLoading}
                    >
                      {isLoading ? "Sending..." : "Send OTP"}
                    </button>
                  </form>
                )}

                {/* STEP 2 – OTP + New Password */}
                {step === 2 && (
                  <form onSubmit={handleResetPassword}>
                    <div className="mb-3">
                      <input
                        type="text"
                        placeholder="Enter OTP"
                        className="form-control cs-input"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        disabled={isLoading}
                      />
                    </div>

                    <div className="mb-3 position-relative">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        placeholder="New Password"
                        className="form-control cs-input password-input"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        disabled={isLoading}
                      />
                      <i
                        className={`fas ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'} password-toggle-icon`}
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      ></i>
                    </div>

                    {/* Password Requirements */}
                    {newPassword && (
                      <div className="mb-3 password-requirements">
                        <p className="mb-1 fw-bold text-muted">Password must contain:</p>
                        <div className="requirements-list">
                          <div className={passwordRules.minLength ? 'requirement-met' : 'requirement-unmet'}>
                            <i className={`fas ${passwordRules.minLength ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                            At least 8 characters
                          </div>
                          <div className={passwordRules.hasUppercase ? 'requirement-met' : 'requirement-unmet'}>
                            <i className={`fas ${passwordRules.hasUppercase ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                            One uppercase letter
                          </div>
                          <div className={passwordRules.hasLowercase ? 'requirement-met' : 'requirement-unmet'}>
                            <i className={`fas ${passwordRules.hasLowercase ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                            One lowercase letter
                          </div>
                          <div className={passwordRules.hasNumber ? 'requirement-met' : 'requirement-unmet'}>
                            <i className={`fas ${passwordRules.hasNumber ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                            One number
                          </div>
                          <div className={passwordRules.hasSpecial ? 'requirement-met' : 'requirement-unmet'}>
                            <i className={`fas ${passwordRules.hasSpecial ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                            One special character (@#$!%*?&)
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="mb-3 position-relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm Password"
                        className="form-control cs-input password-input"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={isLoading}
                      />
                      <i
                        className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'} password-toggle-icon`}
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      ></i>
                    </div>

                    <button
                      type="submit"
                      className="btn w-100 py-3 mb-3 cs-sendbtn"
                      disabled={isLoading}
                    >
                      {isLoading ? "Processing..." : "Reset Password"}
                    </button>
                  </form>
                )}

                <div className="text-center mt-4">
                  <Link to="/login" className="text-decoration-none cs-link-color">
                    <i className="fas fa-arrow-left me-2"></i>
                    Back to Login
                  </Link>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ForgotPasswordComponent;